import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook de gestion de session securise
 * - Logout complet avec clear du cache React Query
 * - Option de deconnexion de tous les appareils (scope: 'global')
 */
export function useSessionManager() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  /**
   * Deconnexion standard
   * - Revoque le token de l'appareil actuel
   * - Vide le cache React Query (donnees de l'ancien utilisateur)
   * - Redirige vers /auth
   */
  const logout = async () => {
    try {
      // 1. Deconnexion Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('[Session] Logout error:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de se deconnecter. Reessayez.',
          variant: 'destructive'
        });
        return;
      }

      // 2. Vider le cache React Query (IMPORTANT: evite les donnees residuelles)
      queryClient.clear();

      // 3. Notification + redirection
      toast({
        title: 'Deconnexion reussie',
        description: 'A bientot !'
      });

      navigate('/auth');
    } catch (error) {
      console.error('[Session] Unexpected logout error:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur inattendue est survenue.',
        variant: 'destructive'
      });
    }
  };

  /**
   * Deconnexion de TOUS les appareils
   * - Revoque tous les refresh tokens de l'utilisateur
   * - Utile si compte compromis ou changement de mot de passe
   */
  const logoutAllDevices = async () => {
    try {
      // scope: 'global' revoque TOUS les refresh tokens
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        console.error('[Session] Global logout error:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de deconnecter tous les appareils.',
          variant: 'destructive'
        });
        return;
      }

      // Vider le cache
      queryClient.clear();

      toast({
        title: 'Deconnecte de tous les appareils',
        description: 'Tous vos appareils ont ete deconnectes.'
      });

      navigate('/auth');
    } catch (error) {
      console.error('[Session] Unexpected global logout error:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur inattendue est survenue.',
        variant: 'destructive'
      });
    }
  };

  return {
    logout,
    logoutAllDevices
  };
}
